// @ts-nocheck
// TODO: tighten types after migration
import React, { RefObject } from "react";
import { FiSmile } from "react-icons/fi";

interface CommentFormProps {
  variant: "blog" | "podcast";
  commentText: string;
  setCommentText: (value: string) => void;
  replyTo: string | null;
  setReplyTo: (value: string | null) => void;
  isPosting: boolean;
  canComment: boolean;
  showTooltip: boolean;
  setShowTooltip: (value: boolean) => void;
  onUnauthenticatedFocus: () => void;
  showEmojiPicker: boolean;
  setShowEmojiPicker: (value: boolean) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onEmojiClick: (emoji: string) => void;
  onLoginClick: () => void;
  onSignupClick: () => void;
  inputRef: RefObject<HTMLTextAreaElement>;
  pickerRef: RefObject<HTMLDivElement>;
}

const EMOJIS = [
  "\u{1F600}", "\u{1F602}", "\u{1F60D}", "\u{1F62E}", "\u{1F622}", "\u{1F621}",
  "\u{1F44D}", "\u{1F44E}", "\u{1F525}", "\u2764\uFE0F", "\u2728", "\u{1F389}",
  "\u{1F680}", "\u{1F4A1}", "\u{1F4AF}", "\u{1F91D}", "\u{1F64C}", "\u2705",
];

export default function CommentForm(props: CommentFormProps) {
  const {
    variant,
    commentText,
    setCommentText,
    replyTo,
    setReplyTo,
    isPosting,
    canComment,
    showTooltip,
    setShowTooltip,
    onUnauthenticatedFocus,
    showEmojiPicker,
    setShowEmojiPicker,
    onSubmit,
    onEmojiClick,
    onLoginClick,
    onSignupClick,
    inputRef,
    pickerRef,
  } = props;

  const wrapperClass =
    variant === "blog"
      ? "textarea-wrapper relative rounded-[12px] border border-[#444] bg-white/5 transition-all duration-300 focus-within:border-[#47b5ff] focus-within:shadow-[0_0_0_3px_rgba(71,181,255,0.1)]"
      : "textarea-wrapper";

  const textareaClass =
    variant === "blog"
      ? "min-h-[56px] w-full resize-y border-none bg-transparent p-[1rem_3.5rem_1rem_1.25rem] font-inherit text-[1rem] leading-[1.5] text-text outline-none placeholder:text-[#666]"
      : undefined;

  const inputIconsClass =
    variant === "blog"
      ? "input-icons absolute right-[1.25rem] top-1/2 z-[5] flex -translate-y-1/2 items-center justify-center"
      : "input-icons";

  const emojiContainerClass =
    variant === "blog"
      ? "emoji-picker-container absolute right-0 top-[calc(100%+5px)] z-[1000] grid max-h-[180px] w-[220px] grid-cols-5 gap-[8px] overflow-x-hidden overflow-y-auto rounded-[12px] border border-slider bg-bg p-[10px] shadow-[0_10px_30px_rgba(0,0,0,0.3)]"
      : "emoji-picker-container";

  return (
    <div className={variant === "blog" ? "comment-input-area mb-[2rem]" : "comment-input-area"}>
      <form onSubmit={onSubmit} className="comment-form">
        <div className={wrapperClass}>
          <textarea
            ref={inputRef}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onFocus={() => setShowTooltip(!canComment)}
            onFocusCapture={() => {
              if (!canComment) {
                onUnauthenticatedFocus();
              }
            }}
            onBlur={() => setTimeout(() => setShowTooltip(false), 200)}
            placeholder={replyTo ? "Replying..." : "What do you think?"}
            rows={1}
            required
            readOnly={!canComment}
            className={textareaClass}
          />
          <div className={inputIconsClass} ref={pickerRef}>
            <FiSmile
              className={variant === "blog" ? "input-icon cursor-pointer text-[1.25rem] transition-colors duration-200 hover:text-[#aaa]" : "input-icon"}
              title="Add emoji"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            />
            {showEmojiPicker && (
              <div className={emojiContainerClass}>
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className={variant === "blog" ? "emoji-btn flex items-center justify-center rounded-[8px] border-none bg-transparent p-[5px] text-[1.5rem] transition-colors duration-200 hover:bg-white/10" : "emoji-btn"}
                    onClick={() => onEmojiClick(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>
          {showTooltip && (
            <div className={variant === "blog" ? "login-tooltip absolute bottom-[110%] left-1/2 z-[100] -translate-x-1/2 whitespace-nowrap rounded-[8px] border border-white/10 bg-[#333] p-[0.75rem_1rem] text-[0.85rem] text-white shadow-[0_10px_25px_rgba(0,0,0,0.5)]" : "login-tooltip"}>
              Please <span onClick={onLoginClick}>Login</span> or <span onClick={onSignupClick}>Signup</span> to enter your comment
            </div>
          )}
        </div>

        {replyTo && (
          <p className={variant === "blog" ? "replying-note mt-[6px] text-[1em] text-text" : "replying-note"}>
            Replying to comment {variant === "blog" ? "-" : "—"}
            <button
              className={variant === "blog" ? "ml-[4px] border-none bg-none p-0 text-[#47b5ff] hover:underline" : undefined}
              type="button"
              onClick={() => setReplyTo(null)}
            >
              Cancel
            </button>
          </p>
        )}

        {canComment && (
          <button
            type="submit"
            disabled={!commentText.trim() || isPosting}
            className={
              variant === "blog"
                ? `post-submit-btn mt-[10px] rounded-[8px] border-none bg-[#47b5ff] px-[16px] py-[8px] font-[600] text-white disabled:cursor-not-allowed disabled:opacity-60 ${isPosting ? "posting" : ""}`
                : `post-submit-btn ${isPosting ? "posting" : ""}`
            }
          >
            {isPosting ? <div className="comment-loader"></div> : "Post"}
          </button>
        )}
      </form>
    </div>
  );
}
